import { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { themeTokens } from './Tokens';

/**
 * SweetAlert2 renders into `document.body`, outside React, so it never saw the theme:
 * every confirm in the app came up on the library's stock white card. Measured in the
 * browser on /training/catalog in dark mode — popup rgb(255,255,255), text rgb(84,84,84),
 * page rgb(17,27,33). It is used 78 times across 47 files, which is why the white card
 * turned up "in some cases" all over the app.
 *
 * Written as inline properties on `<html>` rather than as a rule in `MuiCssBaseline`,
 * which was the first attempt and lost. Two GlobalStyles blocks coexist in this app —
 * measured at DOM style index 16 (dark) and 620 (light), both on the identical selector
 * `html:root`. Specificity cannot break that tie, so the later light one won on every
 * load, and a hard reload reproduced it exactly. An inline property on the element beats
 * both, whatever order the sheets land in.
 *
 * SweetAlert's own `[data-swal2-theme=dark]` palette is deliberately not used: it is a
 * second set of colours to keep in step with ours, and it carries the same tie.
 *
 * The destructive rule below needs a selector, which a custom property cannot express, so
 * it lives in one `<style>` element this component owns and rewrites in place. One element
 * means there is never a second copy to tie with — the failure that sank the first attempt.
 */
const STYLE_ID = 'swal2-theme-rules';

/**
 * Neutral mid-grey for the cancel button, written out rather than read from the palette.
 * This is MUI's own default `grey[700]`; DefaultColors declares only 100-600, so borrowing
 * that step would depend on a scale this app never declares. See the call site below.
 */
const CANCEL_BUTTON_GREY = '#616161';

/**
 * A confirm button that deletes something should not look like one that says OK. The
 * popup carries no icon class of its own, so the rule reaches through `:has()` to the icon
 * SweetAlert renders: `warning` and `error` are the ones that ask before destroying or
 * report a failure, while `success`, `info` and `question` keep the primary colour.
 *
 * The colour is `tokens.destructive`, not `palette.error.main`: the latter is the salmon
 * `#FA896B`, which measured 1.15:1 against `primary.main` — on the button it read as a
 * second shade of the brand orange rather than a warning.
 */
const destructiveRule = (background: string, color: string) => `
  .swal2-popup:has(.swal2-icon.swal2-warning) .swal2-confirm,
  .swal2-popup:has(.swal2-icon.swal2-error) .swal2-confirm {
    background-color: ${background};
    color: ${color};
  }
`;
const SweetAlertTheme = () => {
  const theme = useTheme();

  useEffect(() => {
    const { palette } = theme;
    const vars: Record<string, string> = {
      '--swal2-background': palette.background.paper,
      '--swal2-color': palette.text.primary,
      '--swal2-border': `1px solid ${palette.divider}`,
      '--swal2-footer-color': palette.text.secondary,
      '--swal2-footer-border-color': palette.divider,
      '--swal2-close-button-color': palette.text.secondary,
      '--swal2-input-background': palette.background.default,
      '--swal2-input-border': `1px solid ${palette.grey[400]}`,
      '--swal2-input-focus-border': `1px solid ${palette.secondary.main}`,
      '--swal2-validation-message-background': palette.error.main,
      '--swal2-validation-message-color': palette.error.contrastText,
      // The buttons carried `#3085d6`, which is 3.85:1 under its own white label.
      '--swal2-confirm-button-background-color': palette.primary.main,
      '--swal2-confirm-button-color': palette.primary.contrastText,
      // The cancel button wants a neutral mid-grey that does NOT follow the mode, unlike
      // every quiet surface in the app. That used to read `palette.grey[700]`, which
      // DefaultColors never declares (it stops at 600), so it resolved through MUI's own
      // default scale. Writing the value here instead of borrowing an undeclared step
      // keeps the intent readable and removes the landmine: if the palette is ever frozen
      // to the declared steps, `palette.grey[700]` becomes undefined and getContrastText
      // would throw at runtime rather than degrade visually.
      '--swal2-cancel-button-background-color': CANCEL_BUTTON_GREY,
      // grey has no declared contrastText, so the computed one is the honest source here.
      '--swal2-cancel-button-color': palette.getContrastText(CANCEL_BUTTON_GREY),
      '--swal2-deny-button-background-color': palette.error.main,
      '--swal2-deny-button-color': palette.error.contrastText,
    };

    const root = document.documentElement;
    Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));

    const style = document.getElementById(STYLE_ID)
      || document.head.appendChild(Object.assign(document.createElement('style'), { id: STYLE_ID }));
    const destructive = themeTokens(theme).destructive[palette.mode === 'dark' ? 'dark' : 'light'];
    style.textContent = destructiveRule(destructive.fill, destructive.onFill);
  }, [theme]);

  return null;
};

export default SweetAlertTheme;
